import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { toastError } from "@/lib/userMessages";
import { useRecorderState } from "@/features/recorder/state/recorderState";
import {
  ChatCircleDots as MessageSquarePlus,
  Bug,
  Lightbulb,
  Heart,
  Question as HelpCircle,
  X,
  Upload,
  CircleNotch as Loader2,
  CheckCircle as CheckCircle2,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSupabaseClient } from "@/lib/supabase";
import { useSession } from "@clerk/clerk-react";
import { useProfileId } from "@/features/onboarding/hooks/useProfileId";
import {
  createFeedback,
  uploadFeedbackImage,
  type FeedbackType,
} from "../api/feedbackApi";

const TYPE_OPTIONS: {
  value: FeedbackType;
  label: string;
  icon: React.ReactNode;
  color: string;
}[] = [
  {
    value: "bug",
    label: "Erro / Bug",
    icon: <Bug className="w-4 h-4" />,
    color: "text-red-400",
  },
  {
    value: "suggestion",
    label: "Sugestão",
    icon: <Lightbulb className="w-4 h-4" />,
    color: "text-yellow-400",
  },
  {
    value: "praise",
    label: "Elogio",
    icon: <Heart className="w-4 h-4" />,
    color: "text-pink-400",
  },
  {
    value: "other",
    label: "Outro",
    icon: <HelpCircle className="w-4 h-4" />,
    color: "text-muted-foreground",
  },
];

const MAX_IMAGES = 3;
const MAX_SIZE_MB = 5;

interface ImagePreview {
  file: File;
  url: string;
}

export function FloatingFeedbackButton() {
  const location = useLocation();
  const isAnyRecording = useRecorderState((s) => s.isAnyRecording);
  // Mb3: hide on /simulador (chrome covers the chat composer) and while any
  // recorder is active anywhere in the app (avoid stealing focus mid-capture).
  // Computed before all other hooks so we can early-return AFTER they run.
  const shouldHide = location.pathname.startsWith("/simulador") || isAnyRecording;

  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(false);

  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [images, setImages] = useState<ImagePreview[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { session } = useSession();
  const { client: supabaseClient } = useSupabaseClient();
  const { profileId } = useProfileId();

  const reset = useCallback(() => {
    setType("bug");
    setTitle("");
    setDescription("");
    setSteps("");
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.url));
      return [];
    });
    setSubmitted(false);
    setClosing(false);
  }, []);

  // Auto-close 3s after successful submit: 2.4s visible → 0.6s fade-out → close
  useEffect(() => {
    if (!submitted) return;
    const fadeTimer = setTimeout(() => setClosing(true), 2400);
    const closeTimer = setTimeout(() => {
      setOpen(false);
      reset();
    }, 3000);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(closeTimer);
    };
  }, [submitted, reset]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (submitted) reset();
  }, [submitted, reset]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setSubmitted(false);
  }, []);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const next: ImagePreview[] = [];
    for (const file of Array.from(files)) {
      if (images.length + next.length >= MAX_IMAGES) {
        toast.error(`Máximo de ${MAX_IMAGES} imagens.`);
        break;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`"${file.name}" excede ${MAX_SIZE_MB}MB.`);
        continue;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`"${file.name}" não é uma imagem.`);
        continue;
      }
      next.push({ file, url: URL.createObjectURL(file) });
    }
    setImages((prev) => [...prev, ...next]);
  }, [images.length]);

  const removeImage = useCallback((idx: number) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) { toast.error("Título obrigatório."); return; }
    if (!description.trim()) { toast.error("Descrição obrigatória."); return; }

    setLoading(true);
    try {
      const token = await session?.getToken();
      if (!token) { toast.error("Sessão expirada. Faça login novamente."); return; }

      const imagePaths: string[] = [];
      if (images.length > 0) {
        if (!profileId) {
          toast.error("Sessão não pronta para anexar imagens. Tente novamente.");
          return;
        }
        for (const img of images) {
          const path = await uploadFeedbackImage(supabaseClient, img.file, profileId);
          imagePaths.push(path);
        }
      }

      await createFeedback(token, {
        type,
        title: title.trim(),
        description: description.trim(),
        steps: steps.trim() || undefined,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        image_paths: imagePaths,
      });

      setSubmitted(true);
    } catch (err) {
      toastError(err, "Não foi possível enviar o feedback. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [session, profileId, supabaseClient, type, title, description, steps, images]);

  const currentType = TYPE_OPTIONS.find((t) => t.value === type)!;
  const isBug = type === "bug";

  if (shouldHide) return null;

  return createPortal(
    <>
      {/* Floating trigger button */}
      <button
        onClick={handleOpen}
        aria-label="Enviar feedback"
        className="fixed bottom-6 right-6 z-[9998] flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 text-sm font-medium"
      >
        <MessageSquarePlus className="w-4 h-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {/* Backdrop + panel */}
      {open && (
        <div
          className={`fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4 transition-opacity duration-500 ${
            closing ? "opacity-0" : "opacity-100 animate-in fade-in"
          }`}
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />

          <div
            className={`relative w-full sm:max-w-lg bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden transition-transform duration-500 ${
              closing ? "translate-y-4 sm:scale-95" : "translate-y-0 sm:scale-100 animate-in slide-in-from-bottom-4 sm:zoom-in-95"
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
              <div className="flex items-center gap-2">
                <span className={currentType.color}>{currentType.icon}</span>
                <h2 className="text-sm font-semibold text-foreground">Enviar Feedback</h2>
              </div>
              <button
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {submitted ? (
              /* Success state — animates in, fades out at 2.4s */
              <div
                className={`flex flex-col items-center justify-center gap-4 py-12 px-6 text-center transition-all duration-500 ${
                  closing ? "opacity-0 scale-95" : "opacity-100 scale-100 animate-in fade-in zoom-in-95"
                }`}
              >
                <div className="relative">
                  <CheckCircle2 className="w-14 h-14 text-green-400 drop-shadow-[0_0_12px_rgba(74,222,128,0.5)]" />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground">Feedback enviado!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Obrigado. Lemos tudo com atenção.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => { reset(); setOpen(false); }}>
                  Enviar outro
                </Button>
              </div>
            ) : (
              /* Form */
              <div className="flex flex-col overflow-y-auto">
                <div className="px-5 py-4 space-y-4">
                  {/* Type */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Tipo
                    </label>
                    <Select value={type} onValueChange={(v) => setType(v as FeedbackType)}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10000]">
                        {TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className="flex items-center gap-2">
                              <span className={opt.color}>{opt.icon}</span>
                              {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Title */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Título <span className="text-red-400">*</span>
                    </label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={
                        isBug ? "Ex: Botão não funciona na tela de roteiros" : "Resumo curto"
                      }
                      className="h-9 text-sm"
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Descrição <span className="text-red-400">*</span>
                    </label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={
                        isBug
                          ? "O que aconteceu? O que você esperava que acontecesse?"
                          : "Descreva sua sugestão ou comentário..."
                      }
                      className="text-sm min-h-[80px] resize-none"
                    />
                  </div>

                  {/* Steps to reproduce — only for bugs */}
                  {isBug && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                        Como reproduzir
                        <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-normal normal-case">
                          opcional mas muito útil!
                        </span>
                      </label>
                      <Textarea
                        value={steps}
                        onChange={(e) => setSteps(e.target.value)}
                        placeholder={"1. Abri a página de roteiros\n2. Cliquei em 'Gerar'\n3. A tela ficou em branco"}
                        className="text-sm min-h-[80px] resize-none font-mono text-xs"
                      />
                    </div>
                  )}

                  {/* Image upload */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {isBug ? "Print do erro" : "Imagens"}
                        <span className="ml-1.5 text-[10px] font-normal normal-case text-muted-foreground/70">
                          até {MAX_IMAGES} • máx {MAX_SIZE_MB}MB cada
                        </span>
                      </label>
                      {isBug && (
                        <span className="text-[10px] text-amber-400 flex items-center gap-1">
                          📸 Print ajuda muito a entender o problema
                        </span>
                      )}
                    </div>

                    {/* Previews */}
                    {images.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {images.map((img, i) => (
                          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-border group">
                            <img
                              src={img.url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                            <button
                              onClick={() => removeImage(i)}
                              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {images.length < MAX_IMAGES && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border border-dashed border-border/60 rounded-lg py-3 px-4 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        Clique para adicionar imagem
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                    />
                  </div>

                  {/* Context notice */}
                  <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
                    📍 URL atual e informações do navegador são enviadas automaticamente para facilitar a reprodução do problema.
                  </p>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-border/60 flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={handleClose} disabled={loading}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={handleSubmit} disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        Enviando…
                      </>
                    ) : (
                      "Enviar feedback"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
